import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { has as _has } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import {
  fromPaymentHistoryActions,
  paymentHistoryQuery
} from '@amfam/billing/payment-history/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingAnalytics,
  BillingElementIds,
  BillingPaymentPaths,
  MonthRange,
  Payment,
  PaymentBillAccount,
  PaymentStatus
} from '@amfam/shared/models';
import { CapitalToPascal, CurrencyUSDPipe, PrettyBillingAcctNum } from '@amfam/shared/ui/pipes';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { DsTableUtilService, SidebarService } from '@amfam/ui-kit';

@Component({
  selector: 'ds-payment-history',
  templateUrl: './payment-history.component.html',
  styleUrls: ['./payment-history.component.scss'],
  standalone: false
})
export class PaymentHistoryComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() billAccountNumber: string;
  @Input() accordionIsOpen: boolean;
  @Input() hideForDetails = false;

  @ViewChild('headerCellTpl', { static: true }) headerCellTpl: TemplateRef<unknown>;
  @ViewChild('billAccountTemplate', { static: true }) billAccountTemplate: TemplateRef<unknown>;
  @ViewChild('dateTemplate', { static: true }) dateTemplate: TemplateRef<unknown>;
  @ViewChild('amountTemplate', { static: true }) amountTemplate: TemplateRef<unknown>;
  @ViewChild('paymentMethodTemplate', { static: true }) paymentMethodTemplate: TemplateRef<unknown>;
  @ViewChild('paymentActivityTemplate', { static: true })
  paymentActivityTemplate: TemplateRef<unknown>;
  @ViewChild('paymentHistoryTable') table;

  public cssClasses = 'material noshadow payment-history';
  public headerHeight = '60';
  public rowHeight = 'auto';
  public columns: Array<object> = [];
  public rows: Array<object> = [];
  public pageLimit = 10;
  public footerHeight = '';
  public paymentHistoryForm: UntypedFormGroup;

  public dataLoading$: Observable<boolean>;
  public hasError$: Observable<boolean>;
  public detailRowPayments: unknown = [];

  public sidebarPresent = false;
  public togglePaymentDetails = false;

  // Make the route constants available on the template
  billingPaths = BillingPaymentPaths;

  public monthRanges: { range: MonthRange; text: string }[] = [
    { range: { startMonth: 3, endMonth: 0 }, text: '0-3 Months' },
    { range: { startMonth: 6, endMonth: 3 }, text: '4-6 Months' },
    { range: { startMonth: 9, endMonth: 6 }, text: '7-9 Months' },
    { range: { startMonth: 12, endMonth: 9 }, text: '10-12 Months' },
    { range: { startMonth: 15, endMonth: 12 }, text: '13-15 Months' },
    { range: { startMonth: 18, endMonth: 15 }, text: '16-18 Months' },
    { range: { startMonth: 21, endMonth: 18 }, text: '19-21 Months' },
    { range: { startMonth: 24, endMonth: 21 }, text: '22-24 Months' }
  ];

  isContactBillingToggleEnabled: boolean;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private utilService: UtilService,
    private formBuilder: UntypedFormBuilder,
    private capitalToPascal: CapitalToPascal = new CapitalToPascal(),
    private currencyUSDPipe: CurrencyUSDPipe = new CurrencyUSDPipe(),
    private analyticsFacade: AnalyticsFacade,
    private sidebarService: SidebarService,
    private tableUtil: DsTableUtilService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit() {
    this.isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');

    // This component uses the localized spinner instead of the application level spinner
    this.setupForm();
    this.getColumns();

    // Data is served from the payment history store.
    this.dataLoading$ = this.store.select(paymentHistoryQuery.selectLoading);
    this.hasError$ = this.store.select(paymentHistoryQuery.selectError);

    // Subscribe to data from the payment history store
    combineLatest([
      this.store.select(paymentHistoryQuery.selectMonthRange),
      this.store.select(paymentHistoryQuery.selectPayments),
      this.store.select(BillAccountsSelectors.selectBillAccounts)
    ])
      .pipe(
        map(([monthRange, payments, billAccounts]) => ({
          monthRange,
          payments,
          billAccounts
        })),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.getRows(state.payments, state.billAccounts);
      });
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      this.sidebarService.sidebarPresent$
        .pipe(takeUntil(this.stop$))
        .subscribe(present => (this.sidebarPresent = present));
    }, 0);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnChanges() {
    if (this.accordionIsOpen) {
      this.requestHistory(0);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showDetails(row: any) {
    this.detailRowPayments = row.billAccounts;
    this.togglePaymentDetails = true;
    this.table.rowDetail.collapseAllRows();
    this.table.rowDetail.toggleExpandRow(row);
  }

  hideDetails() {
    this.detailRowPayments = [];
    this.togglePaymentDetails = false;
    this.table.rowDetail.collapseAllRows();
  }

  public onPageChanged() {
    this.utilService.scrollToElementById(BillingElementIds.PAYMENT_HISTORY_ACCORDION);
  }

  private getColumns() {
    this.columns = [
      {
        cellTemplate: this.billAccountTemplate,
        headerTemplate: this.headerCellTpl,
        name: 'Billing Account',
        shortName: 'Account',
        isHeaderOnMobile: true,
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 120
      },
      {
        cellTemplate: this.amountTemplate,
        headerTemplate: this.headerCellTpl,
        name: 'Amount',
        draggable: false,
        isHeaderOnMobile: true,
        resizeable: false,
        canAutoResize: true,
        width: 100
      },
      {
        cellTemplate: this.paymentMethodTemplate,
        headerTemplate: this.headerCellTpl,
        name: 'Payment Method',
        shortName: 'Method',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 200
      },
      {
        cellTemplate: this.paymentActivityTemplate,
        headerTemplate: this.headerCellTpl,
        name: 'Payment Activity',
        shortName: 'Activity',
        draggable: false,
        resizeable: false,
        canAutoResize: true,
        width: 140
      }
    ];

    if (this.billAccountNumber) {
      // remove the first column if rendering for a single bill account
      this.columns.shift();
    }
  }

  // AS : Function to build the rows needed to build datatable for the billing and payments history.
  private getRows(payments: Payment[], billAccounts: BillAccount[]) {
    // Reset the rows
    this.rows = [];

    // Filter the history list if we have a single bill account
    let filteredList: Payment[] = payments.slice();
    if (this.billAccountNumber) {
      filteredList = filteredList.filter(historyItem => {
        const matchingBillAccountPayment = historyItem.billAccounts.find(
          billAccountPayment => billAccountPayment.billAccountNumber === this.billAccountNumber
        );
        return matchingBillAccountPayment ? true : false;
      });
    }

    // Filter out SCHEDULED payments
    filteredList = filteredList.filter(
      historyItem => String(historyItem.paymentStatus).toUpperCase() !== PaymentStatus.SCHEDULED
    );

    // Build out the row object for each history item
    for (const payment of filteredList) {
      // Determine the nickname to show. Default to bundled payment and get more specific
      let displayName = 'Bundled Payment';
      let displayNumber = null;

      if (_has(payment, 'billAccounts') && payment.billAccounts.length === 1) {
        const matchingBillAccount = billAccounts.find(
          billAcct => billAcct.billAccountNumber === payment.billAccounts[0].billAccountNumber
        );

        if (
          matchingBillAccount &&
          _has(matchingBillAccount, 'billingPreferences.accountNickName')
        ) {
          // If we have a nickname, use that.
          displayName = matchingBillAccount.billingPreferences.accountNickName;
          displayNumber = matchingBillAccount.billAccountNumber;
        } else {
          // If we don't have a single matching bill account or it isn't registered, show the potentially masked value in the history list
          displayName = new PrettyBillingAcctNum().transform(
            payment.billAccounts[0].billAccountNumber
          );
        }
      }

      // Determine the nickname for each bill account that would be shown in the detail row
      const billAccountPaymentList = [];
      payment.billAccounts.forEach(item => {
        const billAccountPayment: PaymentBillAccount = Object.assign({}, item);
        const matchingBillAccount: BillAccount = billAccounts.find(
          billAcct => billAcct.billAccountNumber === item.billAccountNumber
        );
        if (_has(matchingBillAccount, 'billingPreferences.accountNickName')) {
          billAccountPayment.nickName = matchingBillAccount.billingPreferences.accountNickName;
        }
        billAccountPaymentList.push(billAccountPayment);
      });

      const rowObject: object = {
        accountReference: {
          displayName: displayName,
          displayNumber: displayNumber
        },
        billAccounts: billAccountPaymentList,
        paymentDate: payment.receivedDate,
        paymentAmount: this.currencyUSDPipe.transform(payment.totalPaymentAmount),
        paymentMethod: this.getDisplayPaymentMethod(
          payment.paymentMethod,
          payment.autoPayIndicator?.toLowerCase() === 'true'
        ),
        paymentType: this.getDisplayPaymentType(payment.paymentType),
        paymentStatus: this.capitalToPascal.transform(payment.paymentStatus),
        paymentAccountNickName: _has(payment, 'paymentAccount.nickName')
          ? payment.paymentAccount.nickName
          : '',
        historyItem: payment
      };

      this.rows.push(rowObject);
    }

    // Sort the rows  by date
    this.rows.sort(
      (a, b) => this.tableUtil.dateComparator(a['paymentDate'], b['paymentDate']) * -1
    );

    // Set the footer row in case we need pagination .
    if (payments.length > this.pageLimit) {
      this.footerHeight = 'auto';
    } else {
      this.footerHeight = '';
    }
  }

  private setupForm() {
    this.paymentHistoryForm = this.formBuilder.group({
      dateRangeMonths: [0, Validators.compose([Validators.required])]
    });

    // AS : listen to changes in the form.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.paymentHistoryForm.valueChanges.pipe(takeUntil(this.stop$)).subscribe((form: any) => {
      // Send analytics on user selection from the drop down.
      const dateRange = BillingAnalytics.paymentHistoryDateRange(
        this.monthRanges[form.dateRangeMonths].text
      );
      this.analyticsFacade.trackPage(dateRange);
      this.requestHistory(form.dateRangeMonths);
    });
  }

  private requestHistory(rangeIndex: number) {
    this.store.dispatch(
      fromPaymentHistoryActions.GetPayments({ monthRange: this.monthRanges[rangeIndex].range })
    );
  }

  private getDisplayPaymentType(paymentType: string) {
    let displayPaymentType = paymentType;
    switch (paymentType) {
      case 'OCR':
        displayPaymentType = 'Check';
        break;
    }
    return displayPaymentType;
  }

  private getDisplayPaymentMethod(paymentMethod: string, isAutopay: boolean) {
    let displayPaymentMethod = paymentMethod;

    switch (paymentMethod) {
      case 'Pay Now':
      case 'Buy Now':
        break;
      case 'PayByPhone':
        displayPaymentMethod = 'Pay By Phone';
        break;
      case 'OTP':
      case 'Check Debit':
      case 'EFT':
        displayPaymentMethod = 'Paid Via Agent';
        break;
      case 'Credit Card':
        displayPaymentMethod = 'Paid Via Agent / Care Center';
        break;
      case 'AFT':
        displayPaymentMethod = 'Automated Funds Transfer';
        break;
      case 'OCR':
        displayPaymentMethod = 'Mailed Payment';
        break;
      case 'Bill Payment Consolidator':
        displayPaymentMethod = 'Paid Via 3rd Party';
        break;
      case 'Online Billing':
        if (isAutopay === true) {
          displayPaymentMethod = 'Automatic Payment';
        } else {
          displayPaymentMethod = 'Electronic Payment';
        }
        break;
    }

    return displayPaymentMethod;
  }
}
